// components/SEOHead.jsx — Dynamic SEO meta tags (client-side update after JS loads)
// این کامپوننت meta tag‌های HTML را بعد از لود JS بروز می‌کند.
// تمام meta tag‌های اولیه (static) در public/index.html هستند که Google قبل از JS می‌خواند.
import { useEffect } from "react";

export default function SEOHead({ content, lang }) {
  useEffect(() => {
    if (!content?.seo) return;

    const seo   = content.seo;
    const isEs  = lang === "es";
    const base  = "https://www.webilo.es";

    // ── helpers ──────────────────────────────────────────────────────────────
    const setMeta = (sel, attr, val) => {
      if (!val) return;
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        const m = sel.match(/\[(\w+(?::\w+)?)=['"]([^'"]+)['"]\]/);
        if (m) el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, val);
    };

    const setLink = (attrs) => {
      const sel  = Object.entries(attrs).map(([k, v]) => `[${k}="${v}"]`).join("");
      let el = document.querySelector(`link${sel}`);
      if (!el) {
        el = document.createElement("link");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    };

    // ── title ────────────────────────────────────────────────────────────────
    const title = isEs
      ? (seo.titleEs || seo.titleEn || "Webilo — Diseño Web en España")
      : (seo.titleEn || "Webilo — Web Design & Frontend Development in Spain");
    document.title = title;

    // ── html lang attribute ───────────────────────────────────────────────────
    document.documentElement.setAttribute("lang", isEs ? "es" : "en");

    // ── description ──────────────────────────────────────────────────────────
    const desc = isEs
      ? (seo.descriptionEs || seo.descriptionEn || "")
      : (seo.descriptionEn || "");
    setMeta("meta[name='description']", "content", desc);

    // ── keywords ─────────────────────────────────────────────────────────────
    const kw = isEs ? (seo.keywordsEs || seo.keywordsEn || "") : (seo.keywordsEn || "");
    setMeta("meta[name='keywords']", "content", kw);

    // ── robots ───────────────────────────────────────────────────────────────
    setMeta("meta[name='robots']", "content", "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");

    // ── canonical ────────────────────────────────────────────────────────────
    setLink({ rel: "canonical", href: `${base}/` });

    // ── hreflang ─────────────────────────────────────────────────────────────
    setLink({ rel: "alternate", hreflang: "es", href: `${base}/?lang=es` });
    setLink({ rel: "alternate", hreflang: "en", href: `${base}/?lang=en` });
    setLink({ rel: "alternate", hreflang: "x-default", href: `${base}/` });

    // ── Open Graph ───────────────────────────────────────────────────────────
    setMeta("meta[property='og:type']",             "content", "website");
    setMeta("meta[property='og:site_name']",        "content", "Webilo");
    setMeta("meta[property='og:locale']",           "content", isEs ? "es_ES" : "en_US");
    setMeta("meta[property='og:url']",              "content", `${base}/`);
    setMeta("meta[property='og:title']",            "content", title);
    setMeta("meta[property='og:description']",      "content", desc);
    if (seo.ogImage) {
      setMeta("meta[property='og:image']",          "content", seo.ogImage);
      setMeta("meta[property='og:image:alt']",      "content", title);
      setMeta("meta[property='og:image:width']",    "content", "1200");
      setMeta("meta[property='og:image:height']",   "content", "630");
    }

    // ── Twitter Card ─────────────────────────────────────────────────────────
    setMeta("meta[name='twitter:card']",        "content", "summary_large_image");
    setMeta("meta[name='twitter:title']",       "content", title);
    setMeta("meta[name='twitter:description']", "content", desc);
    if (seo.ogImage) {
      setMeta("meta[name='twitter:image']",     "content", seo.ogImage);
      setMeta("meta[name='twitter:image:alt']", "content", title);
    }

    // ── Schema.org JSON-LD (dynamic update) ──────────────────────────────────
    // Removes old dynamic script and injects updated one
    const OLD_ID = "webilo-schema-dynamic";
    const old = document.getElementById(OLD_ID);
    if (old) old.remove();

    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ProfessionalService",
          "@id": `${base}/#business`,
          "name": "Webilo",
          "url": base,
          "description": desc || "Diseño web profesional y desarrollo frontend con React en España.",
          "image": seo.ogImage || `${base}/og-image.jpg`,
          "logo": { "@type": "ImageObject", "url": seo.ogImage || `${base}/og-image.jpg` },
          "areaServed": { "@type": "Country", "name": "España" },
          "serviceType": [
            "Diseño Web", "Desarrollo Frontend", "Diseño UI/UX",
            "Landing Page", "Optimización SEO", "Desarrollo React"
          ],
          "priceRange": "$$",
          "email": "hello@webilo.es",
          "inLanguage": ["es", "en"],
          "knowsAbout": [
            "Web Design", "Frontend Development", "React",
            "UI/UX Design", "SEO", "Tailwind CSS", "Figma"
          ]
        },
        {
          "@type": "WebSite",
          "@id": `${base}/#website`,
          "url": base,
          "name": "Webilo",
          "publisher": { "@id": `${base}/#business` },
          "inLanguage": ["es", "en"]
        },
        {
          "@type": "WebPage",
          "@id": `${base}/#webpage`,
          "url": base,
          "name": title,
          "isPartOf": { "@id": `${base}/#website` },
          "about": { "@id": `${base}/#business` },
          "description": desc,
          "inLanguage": isEs ? "es" : "en"
        }
      ]
    };

    const script = document.createElement("script");
    script.id   = OLD_ID;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

  }, [content?.seo, lang]);

  return null;
}
